import { useState, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router';
import clsx from 'clsx';
import type { Project } from '@/lib/types';
import { timeAgo } from '@/lib/format';
import { Bot, Braces, ChevronRight, Globe, ShieldCheck, Smartphone, Zap, type IconComponent } from '@/components/icons';
import { Badge, Card } from '@/components/ui';

/**
 * Test projects grouped by kind of testing, from the project's platform: web and mobile automation, performance
 * testing (projects of their own); the other kinds show as "Coming soon".
 */
const CATEGORIES: { id: string; label: string; Icon: IconComponent; match?: (p: Project) => boolean; note?: string }[] = [
  { id: 'web', label: 'Web Automation', Icon: Globe, match: (p) => p.platform === 'WEB' },
  { id: 'mobile', label: 'Mobile Automation', Icon: Smartphone, match: (p) => p.platform === 'ANDROID' || p.platform === 'IOS' },
  { id: 'performance', label: 'Performance', Icon: Zap, match: (p) => p.platform === 'PERFORMANCE' },
  { id: 'security', label: 'Security Testing', Icon: ShieldCheck, match: (p) => p.platform === 'SECURITY' },
  { id: 'api', label: 'API Testing', Icon: Braces },
  { id: 'llm', label: 'LLM Testing', Icon: Bot },
];

const PLATFORM_LABEL: Record<Project['platform'], string> = { WEB: 'Web', ANDROID: 'Android', IOS: 'iOS', PERFORMANCE: 'Performance', SECURITY: 'Security' };

function group(projects: Project[]) {
  return CATEGORIES.map((c) => ({ ...c, available: !!c.match, projects: c.match ? projects.filter(c.match) : [] }));
}

function Chevron({ open, className }: { open: boolean; className?: string }) {
  return <ChevronRight className={clsx('shrink-0 transition-transform duration-150', open && 'rotate-90', className)} aria-hidden />;
}

/** Collapsible category → project tree for the Test projects page. */
export function ProjectTree({ projects }: { projects: Project[] }) {
  const groups = group(projects);
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string) =>
    setClosed((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  return (
    <Card className="overflow-hidden">
      <ul aria-label="Test projects by kind of testing" className="divide-y divide-border-subtle">
        {groups.map((g) => {
          const open = g.available && !closed.has(g.id);
          const header = (
            <>
              {g.available ? <Chevron open={open} className="size-3 text-fg-muted" /> : <span className="size-3 shrink-0" />}
              <span className={clsx('grid size-8 shrink-0 place-items-center rounded-lg', g.available ? 'bg-primary-subtle text-primary' : 'bg-surface-sunken text-fg-muted')}>
                <g.Icon className="size-3.5" aria-hidden />
              </span>
              <span className={clsx('flex-1 text-xs font-semibold tracking-label uppercase', g.available ? 'text-fg' : 'text-fg-muted')}>{g.label}</span>
              {g.available ? (
                <span className="rounded-full bg-surface-raised px-2 text-[11px]/[18px] font-semibold text-fg-secondary tabular-nums">{g.projects.length}</span>
              ) : (
                <Badge tone={g.note ? 'info' : 'muted'}>{g.note ?? 'Coming soon'}</Badge>
              )}
            </>
          );
          return (
            <li key={g.id}>
              {g.available ? (
                <button
                  type="button"
                  aria-expanded={open}
                  aria-controls={`tree-${g.id}`}
                  onClick={() => toggle(g.id)}
                  className="flex w-full items-center gap-3 px-5 py-3.5 text-left transition hover:bg-surface-sunken"
                >
                  {header}
                </button>
              ) : (
                <div className="flex items-center gap-3 px-5 py-3.5 opacity-70">{header}</div>
              )}
              {open && (
                <ul id={`tree-${g.id}`} className="mb-3 ml-[71px] border-l border-border">
                  {g.projects.length === 0 ? (
                    <li className="relative py-2.5 pr-5 pl-6 text-xs text-fg-muted before:absolute before:top-1/2 before:left-0 before:h-px before:w-4 before:bg-border">
                      No {g.label.toLowerCase()} projects yet.
                    </li>
                  ) : (
                    g.projects.map((p) => (
                      <li key={p.id} className="relative before:absolute before:top-1/2 before:left-0 before:h-px before:w-4 before:bg-border">
                        <Link to={`/projects/${p.id}`} className="group ml-4 flex items-center gap-4 rounded-field py-2.5 pr-4 pl-3 transition hover:bg-surface-sunken">
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-medium text-fg">{p.name}</span>
                            <span className="block truncate text-xs text-fg-muted">{p.platform === 'WEB' || p.platform === 'PERFORMANCE' ? p.targetUrl : p.appId}</span>
                          </span>
                          {p._count?.tests !== undefined && (
                            <span className="hidden text-xs text-fg-secondary tabular-nums sm:inline">
                              {p._count.tests} {p._count.tests === 1 ? 'test' : 'tests'}
                            </span>
                          )}
                          {g.id === 'mobile' && <Badge>{PLATFORM_LABEL[p.platform]}</Badge>}
                          <span className="hidden w-28 text-right text-xs text-fg-muted md:inline">{timeAgo(p.updatedAt)}</span>
                          <ChevronRight className="size-3 text-fg-muted transition group-hover:translate-x-0.5 group-hover:text-fg" aria-hidden />
                        </Link>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

const SIDEBAR_LIMIT = 6;

/** Compact tree under "Test projects" in the sidebar. */
export function SidebarProjectTree({ projects, onNavigate }: { projects: Project[]; onNavigate?: () => void }) {
  const groups = group(projects);
  const [closed, setClosed] = useState<ReadonlySet<string>>(new Set());
  const toggle = (id: string) =>
    setClosed((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  const row = (children: ReactNode) => <span className="flex min-w-0 flex-1 items-center gap-2">{children}</span>;

  return (
    <ul aria-label="Test projects by kind of testing" className="pb-2">
      {groups.map((g) => {
        const open = g.available && !closed.has(g.id);
        return (
          <li key={g.id}>
            {g.available ? (
              <button
                type="button"
                aria-expanded={open}
                aria-controls={`side-tree-${g.id}`}
                onClick={() => toggle(g.id)}
                className="flex w-full items-center gap-2 py-1.5 pr-5 pl-[42px] text-left text-[11px] font-semibold tracking-label text-fg-secondary uppercase transition hover:text-fg"
              >
                {row(
                  <>
                    <Chevron open={open} className="size-2.5" />
                    <span className="truncate">{g.label}</span>
                  </>,
                )}
                <span className="text-fg-muted tabular-nums">{g.projects.length}</span>
              </button>
            ) : (
              <div className="flex items-center gap-2 py-1.5 pr-5 pl-[42px] text-[11px] font-semibold tracking-label text-fg-muted uppercase opacity-70">
                {row(
                  <>
                    <span className="size-2.5 shrink-0" />
                    <span className="truncate">{g.label}</span>
                  </>,
                )}
                <span className="tracking-normal normal-case" title={g.note}>
                  {g.note ? 'in projects' : 'soon'}
                </span>
              </div>
            )}
            {open && g.projects.length > 0 && (
              <ul id={`side-tree-${g.id}`} className="ml-[46px] border-l border-border">
                {g.projects.slice(0, SIDEBAR_LIMIT).map((p) => (
                  <li key={p.id}>
                    <NavLink
                      to={`/projects/${p.id}`}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        clsx('-ml-px block truncate border-l py-1.5 pr-5 pl-3 text-xs transition', isActive ? 'border-primary text-primary' : 'border-transparent text-fg-secondary hover:text-fg')
                      }
                    >
                      {p.name}
                    </NavLink>
                  </li>
                ))}
                {g.projects.length > SIDEBAR_LIMIT && (
                  <li>
                    <Link to="/projects" onClick={onNavigate} className="block py-1.5 pr-5 pl-3 text-xs text-fg-muted hover:text-fg">
                      +{g.projects.length - SIDEBAR_LIMIT} more
                    </Link>
                  </li>
                )}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}
